/**
 * UserProfilePage - displays user profile with photo grid
 */

import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { useUserById, useUserPhotos } from '../services/users';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { PhotoDetailModal } from '../components/photos/PhotoDetailModal';
import type { Photo } from '../types';

export const UserProfilePage = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);

  const {
    data: user,
    isLoading: isLoadingUser,
    isError: isUserError,
    error: userError,
  } = useUserById(userId);

  const {
    data: photosData,
    isLoading: isLoadingPhotos,
    isError: isPhotosError,
    error: photosError,
    refetch: refetchPhotos,
  } = useUserPhotos(userId);

  const isOwnProfile = currentUser?.userId === userId;

  // Loading state
  if (isLoadingUser) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading profile...</p>
      </div>
    );
  }

  // Error state - user not found
  if (isUserError) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{userError?.message || 'Failed to load user profile'}</AlertDescription>
        </Alert>
        <Button onClick={() => navigate(-1)} variant="outline">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Go Back
        </Button>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const photos = photosData?.items || [];
  const photoCount = photos.length;

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8">
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(-1)}
        className="mb-6"
        data-testid="back-button"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back
      </Button>

      {/* Profile Header */}
      <div className="mb-8 flex flex-col items-center gap-6 md:flex-row md:items-start">
        {/* Avatar */}
        <Avatar className="h-32 w-32 md:h-40 md:w-40">
          <AvatarImage src={user.avatarUrl} alt={`${user.displayName}'s profile photo`} />
          <AvatarFallback className="text-4xl">
            {user.displayName
              .split(' ')
              .map((n) => n[0])
              .join('')
              .toUpperCase()}
          </AvatarFallback>
        </Avatar>

        {/* User info */}
        <div className="flex-1 text-center md:text-left">
          <h1 className="mb-2 text-3xl font-bold">{user.displayName}</h1>
          <p className="mb-4 text-muted-foreground">{user.email}</p>

          <div className="flex flex-wrap justify-center gap-6 text-sm md:justify-start">
            <div>
              <span className="font-semibold">{photoCount}</span>{' '}
              <span className="text-muted-foreground">{photoCount === 1 ? 'photo' : 'photos'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Member since </span>
              <span className="font-semibold">
                {new Date(user.createdAt).toLocaleDateString('en-US', {
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
            </div>
          </div>

          {/* Edit profile button (only for own profile) */}
          {isOwnProfile && (
            <Button className="mt-4" variant="outline" onClick={() => navigate('/profile')}>
              Edit Profile
            </Button>
          )}
        </div>
      </div>

      {/* Photos section */}
      <div className="border-t pt-8">
        <h2 className="mb-4 text-xl font-semibold">Photos</h2>

        {/* Loading photos */}
        {isLoadingPhotos && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Photos error */}
        {isPhotosError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>Failed to load photos. {photosError?.message}</span>
              <Button variant="outline" size="sm" onClick={() => refetchPhotos()}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Empty state */}
        {!isLoadingPhotos && !isPhotosError && photos.length === 0 && (
          <div
            className="flex flex-col items-center justify-center gap-4 py-12 text-center"
            data-testid="empty-state"
          >
            <div className="text-6xl">📷</div>
            <h3 className="text-lg font-semibold">No photos yet</h3>
            <p className="text-sm text-muted-foreground">
              {isOwnProfile
                ? 'Upload your first photo to get started!'
                : `${user.displayName} hasn't posted any photos yet.`}
            </p>
          </div>
        )}

        {/* Photo grid */}
        {photos.length > 0 && (
          <div
            className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 md:gap-4"
            data-testid="photo-grid"
          >
            {photos.map((photo: Photo) => (
              <div
                key={photo.photoId}
                className="group relative aspect-square cursor-pointer overflow-hidden rounded-lg bg-muted"
                onClick={() => setSelectedPhotoId(photo.photoId)}
                data-testid="photo-grid-item"
              >
                {/* Photo thumbnail */}
                <img
                  src={photo.images.thumbnail}
                  alt={photo.caption || 'Photo'}
                  className="h-full w-full object-cover transition-transform group-hover:scale-105"
                  loading="lazy"
                />

                {/* Hover overlay with stats */}
                <div
                  className="absolute inset-0 flex items-center justify-center gap-4 bg-black/50 opacity-0 transition-opacity group-hover:opacity-100"
                  data-testid="photo-overlay"
                >
                  <div className="flex items-center gap-1 text-white">
                    <span className="text-lg">❤️</span>
                    <span className="font-semibold">{photo.likeCount}</span>
                  </div>
                  <div className="flex items-center gap-1 text-white">
                    <span className="text-lg">💬</span>
                    <span className="font-semibold">{photo.commentCount}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Photo detail modal */}
      <PhotoDetailModal
        storyId={selectedPhotoId}
        open={!!selectedPhotoId}
        onOpenChange={(open) => !open && setSelectedPhotoId(null)}
      />
    </div>
  );
};
